<template>

<div class="main marquees">

	<div class="list-view">

		<div class="sub-header">
			<div class="input-with-control">
				<input type="text" ref="search_term" v-model="filters.search_term" placeholder="Search..." @keyup.enter="setFilter()" v-focus>
				<div class="button action input-go" @click="setFilter()"><i class="fas fa-search"></i></div>
			</div>
			<div class="controls">
				<pagination :paging="paging" @updatePage="goToPage"></pagination>
			</div>
		</div>

		<div v-if="showFilters" class="filters">

			<div class="title">Filter marquees you have set for these page types...</div>
			<ul class="checkbox-group">
				<li>
					<span :class="['pointer', 'far', filters.home_page ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('home_page')"></span>
					<label>Home Page</label>
				</li>
			</ul>
			<select v-model="filters.department_group_id" @change="setFilter()">
				<option value="">Group</option>
				<option v-for="department_group in department_groups" :value="department_group.department_group_id">{{ department_group.description }}</option>
			</select>
			<select v-model="filters.department_id" @change="setFilter()">
				<option value="">Department</option>
				<option v-for="department in departments" :value="department.department_id">{{ department.description }}</option>
			</select>
			<select v-model="filters.category_id" @change="setFilter()">
				<option value="">Category</option>
				<option v-for="category in categories.filter(filteredCategories)" :value="category.category_id">{{ category.description }}</option>
			</select>
			<select v-model="filters.manufacturer_id" @change="setFilter()">
				<option value="">Manufacturer</option>
				<option v-for="manufacturer in manufacturers" :value="manufacturer.manufacturer_id">{{ manufacturer.description }}</option>
			</select>

			<div class="title">Only show marquees linked to currently available products...</div>
			<ul class="checkbox-group">
				<li>
					<span :class="['pointer', 'far', filters.available ? 'fa-check-square' : 'fa-square']" @click="toggleFilter('available')"></span>
					<label>Available</label>
				</li>
			</ul>

			<div :class="['button', hasFilters ? 'neutral' : 'disabled' ]" @click="clearFilters(true)">Clear Filters</div>

			<div class="divider"></div>
			<div v-if="autoPilotEnabled" class="label">
				<p>These marquees are part Auto Pilot and cannot be changed.</p>
				<router-link to="/website/auto-pilot">Manage Auto Pilot...</router-link>
			</div>

			<div v-if="marqueeDetail" class="button neutral" @click="clearDetail()">Back to List</div>

			<div v-if="!autoPilotEnabled">
				<router-link class="button neutral" to="/website/marquees/new" tag="div"><span>Create Marquee</span></router-link>
				<div class="divider"></div>
				<div v-if="filters.library_only" class="title">Click to see your current marquees...</div>
				<div v-else class="title">Click to see all other marquees available...</div>
				<button class="button neutral" @click="showLibrary()">{{ filters.library_only ? 'Current' : 'Available' }} Marquees</button>
			</div>

		</div>

		<loading v-if="loading" class="main-content">
		    Loading: Marquees<br />
		</loading>

		<div v-else-if="!marqueeDetail" class="list">
			<h2 v-if="!filters.library_only">These marquees are used {{ getAdministratorFlag ? 'in auto-pilot' : 'on your website' }}</h2>
			<h2 v-if="filters.library_only">These marquees {{ getAdministratorFlag ? 'can be used for auto-pilot' : 'are available for your website' }}</h2>
			<div v-if="marquees.length > 0">
				<draggable handle=".drag" :sort="true" v-model="marquees" @end="endDrag" group="marquee_group" tag="div" class="list-grid">
					<div v-for="marquee in marquees" :key="marquee.marquee_id" class="cell clickable-cell">
						<router-link :to="'/website/marquees/' + marquee.marquee_id">
							<img v-if="marquee.image_src" :src="marquee.image_src">
							<div v-else class="no-image">No Image</div>
							<div class="title">{{ marquee.description }}</div>
							<div v-if="hasFilters && !filters.search_term && marquees.length > 0" class="drag"><i class="fas fa-grip-horizontal"></i></div>
							<div v-if="marquee.product_id" class="meta" style="float: right; margin: 0 1em 0 0;">Available: <span :class="{unavailable: marquee.available == 0}">{{ marquee.available }}</span></div>
							<div class="meta" style="float: left; margin: 0 1em 0 0;">{{ marquee.client_id ? 'Custom' : 'Library' }} marquee, set for {{ marquee.used_on_pages }} page type{{ marquee.used_on_pages == 1 ? '' :'s' }}</div>
						</router-link>
					</div>
				</draggable>
			</div>
			<div v-else class="empty-list">
				This list is empty
			</div>

			<div v-if="hasFilters && marquees.length > 0 && message" class="message detail-controls">
				{{ message }}
			</div>
		</div>

		<div v-if="marqueeDetail" class="detail">

			<img v-if="!showDropZone" :src="marquee.image_src">
			<div v-if="userCanEdit" class="swap-marquee-image">
				<vue-dropzone v-if="showDropZone" @vdropzone-success="marqueeUploadSuccess" @vdropzone-error="marqueeUploadError" ref="marqueeDropzone" id="marquee_dropzone" :options="marqueeDropzoneOptions"></vue-dropzone>
				<div v-else></div>
			</div>

			<div class="four-one-columns">
				<label>Description</label>
				<label></label>
				<input type="text" v-model="marquee.description" :disabled="!userCanEdit">
				<div>
					<div v-if="marquee.image_src && userCanEdit" :class="['button', showDropZone ? 'cancel' : 'neutral']" @click="showDropZone = !showDropZone">{{ showDropZone ? 'Cancel Change Image' : (marquee.image_src ? 'Change Image' : 'Add Image') }}</div>
				</div>
			</div>

			<div class="two-columns" style="align-items: center;">
				<div>
					<label>Link Type</label>
					<select v-model="marquee.link_type_id" @change="getLinksToList()" :disabled="!userCanEdit">
						<option value=''>Select...</option>
						<option v-for="(linkType, linkTypeId) in linkTypes" :value="linkTypeId">{{ linkType.description }}</option>
					</select>
				</div>
				<div class="note">Set the Link Type for this marquee, e.g. Department or Product.</div>
				<div v-if="marquee.link_type_id && linkTypes[marquee.link_type_id].link_type_code != 'HOME'">
					<label>Links To</label>
					<input v-if="linkTypes[marquee.link_type_id].link_type_code == 'EXTERNAL'" type="text" v-model="marquee.link_url" :disabled="!userCanEdit">
					<div v-else-if="linksToList.length == 0 && linkTypes[marquee.link_type_id].link_type_code == 'MANUFACTURER'" class="input-with-control">
						<input type="text" v-model="marquee.list_search_text" placeholder="Search manufacturers..." @keyup.enter="searchManufacturers()" :disabled="!userCanEdit">
						<div :class="['button', !userCanEdit ? 'disabled' : 'action', 'input-go']" @click="searchManufacturers()">
							<i :class="searchLoading ? 'fas fa-spinner fa-spin' : 'fas fa-search'"></i>
						</div>
					</div>
					<div v-else-if="linksToList.length == 0 && linkTypes[marquee.link_type_id].link_type_code == 'PRODUCT'">
						<div class="input-with-control">
							<input type="text" v-model="marquee.list_search_text" placeholder="Search products..." @keyup.enter="searchProducts()" :disabled="!userCanEdit">
							<div :class="['button', !userCanEdit ? 'disabled' : 'action', 'input-go']" @click="searchProducts()">
								<i :class="searchLoading ? 'fas fa-spinner fa-spin' : 'fas fa-search'"></i>
							</div>
						</div>
						<div class="two-columns">
							<div>
								<label>Product ID</label>
								<input type="text" v-model="marquee.link_type_value" disabled>
							</div>
							<div>
								<label>UPC</label>
								<input type="text" v-model="marquee.upc" disabled>
							</div>
						</div>
					</div>

					<select v-else-if="linkTypes[marquee.link_type_id].link_type_code == 'DEPARTMENT_GROUP'" v-model="marquee.link_type_value">
						<option value=''>Select...</option>
						<option v-for="(linksTo, key) in linksToList" :value="linksTo.department_group_id">{{ linksTo.description }}</option>
					</select>
					<select v-else-if="linkTypes[marquee.link_type_id].link_type_code == 'DEPARTMENT'" v-model="marquee.link_type_value">
						<option value=''>Select...</option>
						<option v-for="(linksTo, key) in linksToList" :value="linksTo.department_id">{{ linksTo.description }}</option>
					</select>
					<select v-else-if="linkTypes[marquee.link_type_id].link_type_code == 'CATEGORY'" v-model="marquee.link_type_value">
						<option value=''>Select...</option>
						<option v-for="(linksTo, key) in linksToList" :value="linksTo.category_id">{{ linksTo.description }}</option>
					</select>
					<select v-else-if="linkTypes[marquee.link_type_id].link_type_code == 'PAGE'" v-model="marquee.link_type_value">
						<option value=''>Select...</option>
						<option v-for="(linksTo, key) in linksToList" :value="linksTo.page_id">{{ linksTo.description }}</option>
					</select>
					<select v-else-if="linkTypes[marquee.link_type_id].link_type_code == 'PRODUCT_TAG'" v-model="marquee.link_type_value">
						<option value=''>Select...</option>
						<option v-for="(linksTo, key) in linksToList" :value="linksTo.product_tag_id">{{ linksTo.description }}</option>
					</select>
					<select v-else-if="linkTypes[marquee.link_type_id].link_type_code == 'PRODUCT'" v-model="marquee.link_type_value">
						<option value=''>Select...</option>
						<option v-for="(linksTo, key) in linksToList" :value="linksTo.product_id">{{ linksTo.description }}</option>
					</select>
					<select v-else-if="linkTypes[marquee.link_type_id].link_type_code == 'MYACCOUNT'" v-model="marquee.link_type_value">
						<option value=''>Select...</option>
						<option v-for="(linksTo, key) in linksToList" :value="linksTo.description">{{ linksTo.description }}</option>
					</select>

				</div>
				<div class="note"><span v-if="marquee.link_type_id && linkTypes[marquee.link_type_id].link_type_code != 'HOME'">Enter the links to for this marquee.</span></div>
				<div v-if="this.marquee.product_id">{{ "Product Id: " + this.marquee.product_id }}</div>
				

			</div>

			<div class="add-remove">
				<div>
					<div class="heading">Include this marquee on a page type</div>
					<select v-model="add_to" @change="setAddTo()" :disabled="autoPilotEnabled">
						<option value="">Select...</option>
						<option value="home">Home Page</option>
						<option value="department_group">Department Group</option>
						<option value="department">Department</option>
						<option value="category">Category</option>
						<option value="manufacturer">Manufacturer</option>
					</select>
					<div v-if="showAddToSearch" class="input-with-control">
						<input type="text" v-model="add_to_search" placeholder="Search..." @keyup.enter="searchToAdd()">
						<div class="button action input-go" @click="searchToAdd()"><i class="fas fa-search"></i></div>
					</div>
					<div v-if="addToList.length > 0">
						<table class="items-available">
							<tr v-for="(available, index) in addToList">
								<td>{{ available.id }}</td>
								<td>{{ available.description }}</td>
								<td><button class="primary" @click="addMarqueePage(index)"><i class="fas fa-chevron-right"></i></button></td>
							</tr>
						</table>
					</div>
				</div>
				<div>
					<div class="heading">Page types selected</div>
					<table class="items-selected">
						<tr v-for="(marquee_page, index) in marquee_pages">
							<td><button :class="autoPilotEnabled ? 'disabled' : 'delete'" @click="removeMarqueePage(index)"><i class="fas fa-times link pointer"></i></button></td>
							<td>{{ marquee_page.marquee_page_type }}</td>
							<td>{{ marquee_page.marquee_page_description }}</td>
						</tr>
					</table>
				</div>
			</div>

			<div v-if="!autoPilotEnabled" class="controls detail-controls">
				<button class="button neutral spaced" @click="clearDetail()">Back to List</button>
				<button class="button primary spaced" @click="saveMarquee()"><span>Save Marquee</span></button>
				<button class="button delete spaced" @click="deleteMarquee()"><span>Delete Marquee</span></button>
				<div v-if="saving" class="message"><i class="fas fa-spinner fa-spin"></i></div><div v-else class="message">{{ message }}</div>
			</div>

		</div>

	</div>

	<help-popup :helpLink="{81:'Marquee Banners', 376:'Add a Marquee Banner to Your Website', 375:'Create a New Marquee Banner', 105:'Website Template: Marquees Section'}" :tours="false"></help-popup>

</div>

</template>

<script>
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import HelpPopup from '@/components/HelpPopup.vue'

export default {
	name: 'marquees',
	props: ['marquee_id'],
    components: {
        'vue-dropzone': vue2Dropzone,
		'help-popup': HelpPopup
    },
	data() {
		return {
			loading: true,
			searchLoading: false,
			showFilters: true,
			filters: {
				page: 1,
				search_term: '',
				home_page: false,
				available: false,
				department_group_id: '',
				department_id: '',
				category_id: '',
				manufacturer_id: '',
				library_only: false
			},
			paging: {},
			message: '',
			clientId: '',
			marquees: [],
			definition: [],
			department_groups: [],
			departments: [],
			categories: [],
			manufacturers: [],
			marqueeDetail: '',
			marquee: {
                image_src: '',
                client_id: '',
                description: '',
                link_type: '',
                link_url: '',
                list_search_text: '',
				product_id: ''
            },
			marquee_pages: [],
			showDropZone: false,
			add_to: '',
			showAddToSearch: false,
			add_to_search: '',
			addToList: [],
			listDefinitions: {},
			saving: false,
			marqueeSaved: false,
			linkTypes: {},
			linksToList: [],
			autoPilotEnabled: false
		}
	},
	created() {
		this.getAutoPilotEnabled()
		this.getLinkTypes()
		this.getFilters()
        this.updateCustomRouteName()
	},
	mounted() {
    	if (this.marquee_id) {
	    	this.marqueeDetail = this.marquee_id
			this.getDetail()
    	} else {
			this.getList()
		}
	},
	watch: {
		marquee_id: function(to, from) {
	    	this.marqueeDetail = this.marquee_id
	    	if (this.marqueeDetail) {
				this.getDetail()
			}
		},
		getClientId: function(to, from) {
			if (to && this.marquee_id == 'new') {
				this.marquee.client_id = this.getClientId
			}
		}
	},
	computed: {
		userCanEdit() {
			return this.marquee.client_id == this.clientId || this.getAdministratorFlag
		},
		hasFilters() {
			for (const [key, value] of Object.entries(this.filters)) {
				if (['page', 'fd_token', 'source', 'hostname', 'app_route', 'library_only'].includes(key)) { continue }
				if (key == 'start_date' && value == this.default_start_date) { continue }
				if (key == 'end_date' && value == this.default_end_date) { continue }
				if (value) {
					return true
				}
			}
			return false
		},
		marqueeDropzoneOptions() {
			return {
				url: this.getImageUploadAPI,
				withCredentials: true,
				uploadMultiple: false,
				paramName: 'upload_file',
				maxFilesize: 2.0,
				addRemoveLinks: true,
				dictDefaultMessage: 'Drop a new marquee image here.<br><br>A 3:1 ratio is best, e.g. 1800 x 600 px<br>and jpg images are preferred.',
				minHeight: 250,
				params: {
					fd_token: this.$getCookie('dashboard_token'),
					source: 'WEBSITE',
					context: 'MARQUEE',
					variation: 'large',
					image_id: this.marquee.preview_image_id || ''
				}
			}
		},
		userCanEdit() {
			return this.marquee.client_id == this.clientId || this.getAdministratorFlag
		}
	},
	methods: {
		getAutoPilotEnabled() {
			this.apiCall({ destination: 'get_item_preference', data: { preference_code: 'ENABLE_AUTO_PILOT' } }).then(obj => {
				this.autoPilotEnabled = obj.ENABLE_AUTO_PILOT != ''
			}).catch(err => {
				console.log(err)
			})
		},
		filteredCategories(category) {
			return this.filters.department_id == '' || category.department_id == this.filters.department_id;
		},
		getFilters() {
			this.apiCall({ destination: 'get_list_marquee_filters' }).then(obj => {
				this.department_groups = obj.department_groups
				this.departments = obj.departments
				this.categories = obj.categories
				this.manufacturers = obj.manufacturers
			}).catch(err => {
				console.log(err)
			})
		},
		showLibrary() {
			let currentFilter = this.filters.library_only
			this.clearFilters()
			this.filters.library_only = !currentFilter
			this.getList()
		},
		getList() {
			this.message = ''
			this.marqueeDetail = ''
			this.loading = true
			if (this.marquee_id) {
				this.$router.push('/website/marquees')
			}
			this.apiCall({ destination: 'get_list_marquees', data: this.filters }).then(obj => {
				this.loading = false
				if (obj.marquees) {
					this.marquees = obj.marquees
					this.paging = obj.paging
					this.definition = obj.definition
					this.clientId = obj.client_id
				}
			}).catch(err => {
				console.log(err)
			})
		},
		setFilter() {
			this.filters.page = 1
			this.getList()
		},
		endDrag(event) {
			var marquees = []
			marquees = this.marquees.map((marquee, index) => {
				marquee.sort_order = index + 1
				return marquee
			})
			this.marquees = marquees
			this.saveMarqueeOrder()
		},
		saveMarqueeOrder() {
			this.message = ''
			this.apiCall({ destination: 'save_marquee_order', data: { marquees: this.marquees, filters: this.filters } }).then(obj => {
				this.message = obj.message || 'Marquee order saved!'
			}).catch(err => {
				console.log(err)
			})
		},
		getDetail() {
			this.marquee = {}
			this.marquee_pages = []
			this.listDefinitions = {}
			this.addToList = []
			this.add_to_search = ''
			this.add_to = ''
			if (this.marquee_id == 'new') {
				this.marquee.client_id = this.getClientId
				this.showDropZone = true
				this.loading = true
				this.apiCall({ destination: 'get_item_marquee', data: { marquee_id: this.marqueeDetail } }).then(obj => {
					this.loading = false
					this.listDefinitions = obj.definitions
					this.getLinksToList()
				}).catch(err => {
					this.loading = false
					console.log(err)
				})
			} else {
				this.loading = true
				this.apiCall({ destination: 'get_item_marquee', data: { marquee_id: this.marqueeDetail } }).then(obj => {
					this.loading = false
					if (obj.marquee) {
						this.marquee = this.$copy(obj.marquee)
						this.marquee_pages = obj.marquee_pages
						this.listDefinitions = obj.definitions
						this.showDropZone = this.marquee.client_id == this.clientId && !this.marquee.image_src
						this.getLinksToList()
					}
				}).catch(err => {
					this.loading = false
					console.log(err)
				})
			}
		},
		clearDetail() {
			this.marqueeDetail = ''
			this.add_to = ''
			this.add_to_search = ''
			this.addToList = []
			this.$router.push('/website/marquees')
			if (this.marqueeSaved || this.marquees.length == 0) {
				this.getFilters()
				this.getList()
				this.marqueeSaved = false
			}
		},
		goToPage(page) {
			this.filters.page = page
			this.getList()
		},
		clearFilters(getList) {
			let currentFilter = this.filters.library_only
			this.filters = {
				page: 1,
				search_term: '',
				home_page: false,
				department_group_id: '',
				department_id: '',
				category_id: '',
				manufacturer_id: '',
				library_only: currentFilter
			}
			if (getList) {
				this.getList()
			}
		},
		toggleFilter(which) {
			this.filters[which] = !this.filters[which]
			if (this.filters.home_page) {
				this.filters.department_group_id = ''
				this.filters.department_id = ''
				this.filters.category_id = ''
				this.filters.manufacturer_id = ''
			}
			this.getList()
		},
		setAddTo() {
			this.addToList = []
			this.add_to_search = ''
			switch (this.add_to) {
				case 'home':
					this.addHome()
					break
				case 'department_group':
					this.searchToAdd()
					this.showAddToSearch = false
					break
				case 'department':
				case 'manufacturer':
				case 'category':
					this.showAddToSearch = true
					break
				default:
					this.showAddToSearch = false
					break
			}
		},
		addHome() {
			// is it already in the selected list?
			let inList = false
			for (var i = 0; i < this.marquee_pages.length; i++) {
				if (this.marquee_pages[i]['marquee_page_type_code'] == 'HOME') {
					inList = true
				}
			}
			if (!inList) {
				this.marquee_pages.push({marquee_page_id: '', marquee_page_type: 'Home Page', marquee_page_type_code: 'HOME', marquee_page_description: '', id: ''})
			}
			this.add_to = ''
		},
		searchToAdd() {
			this.addToList = []
			let getList = this.listDefinitions[this.add_to]['getList']
			let listId = this.listDefinitions[this.add_to]['listId']
			//let pageTypeCode = this.listDefinitions[this.add_to]['pageTypeCode']
			this.apiCall({ destination: 'get_list_' + getList, data: { search_term: this.add_to_search } }).then(obj => {
				var inList
				for (var i = 0; i < obj[getList].length; i++) {
					// is it already in the selected list?
					inList = false
					for (var q = 0; q < this.marquee_pages.length; q++) {
						if (this.marquee_pages[q][listId] == obj[getList][i][listId]) {
							inList = true
							break
						}
					}
					if (!inList) {
						this.addToList.push(obj[getList][i])
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		addMarqueePage(index) {
			this.marquee_pages.push({
				marquee_page_id: '',
				marquee_page_type: this.listDefinitions[this.add_to]['name'],
				marquee_page_type_code: this.listDefinitions[this.add_to]['pageTypeCode'],
				marquee_page_description: this.addToList[index]['description'],
				marquee_page_type_value: this.addToList[index][this.listDefinitions[this.add_to]['listId']],
			})
			this.addToList.splice(index, 1)
		},
		removeMarqueePage(index) {
			if (this.autoPilotEnabled) { return }
			this.marquee_pages.splice(index, 1)
		},
		marqueeUploadSuccess(file, response) {
			response = JSON.parse(response)
            // TODO: is this redundant with store.js?
			// if (response.status == 'login_required') {
			// 	this.$store.commit('UPDATE_LOGIN_REQUIRED', true)
	        // 	var location = '/login?return=' + encodeURI(document.location.pathname + document.location.search)
        	// 	document.location.replace(location)
			// }
			// set display to new image
			this.showDropZone = false
			this.marquee.preview_image_id = response.image.image_id || ''
			this.marquee.image_src = response.image.image_src || ''
		},
		marqueeUploadError(file, message, xhr) {
			console.log('marqueeUploadError')
			console.log(file)
			console.log(message)
			console.log(xhr)
		},
		saveMarquee() {
			this.saving = true
			this.apiCall({ destination: 'save_item_marquee', data: { marquee: this.marquee, marquee_pages: this.marquee_pages } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.marqueeSaved = true
					if (this.marquee_id == 'new') {
						this.$router.replace('/website/marquees/' + obj.marquee_id)
					} else {
						this.getDetail()
					}
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		deleteMarquee() {
			this.saving = true
			this.apiCall({ destination: 'delete_item_marquee', data: { marquee_id: this.marquee.marquee_id } }).then(obj => {
				this.saving = false
				if (obj.status == 'success') {
					this.getList()
				} else {
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		},
		getLinkTypes() {
			this.apiCall({ destination: 'get_list_link_types' }).then(obj => {
				this.linkTypes = obj.link_types
			}).catch(err => {
				console.log(err)
			})
		},
		getLinksToList() {
			if (!this.marquee.link_type_id) {
				return false
			}
			this.linksToList = []
			if (['EXTERNAL', 'PRODUCT', 'MANUFACTURER'].includes(this.linkTypes[this.marquee.link_type_id].link_type_code)) {
				return false
			}
			// NOTE: this has to be after the above, otherwise every time we fetch a marquee, the link_type gets cleared out
			this.marquee.link_url = '' // reset url on change -- API will parse when saved
			this.apiCall({ destination: 'get_list_links_to', data: { link_type: this.linkTypes[this.marquee.link_type_id].link_type_code }} ).then(obj => {
				this.loading = false
				if (obj.status == 'error') {
					this.message = obj.message
				} else {
					this.linksToList = obj.links_to
				}
			}).catch(err => {
				console.log(err)
			})
		},
		searchManufacturers() {
			if (this.marquee.client_id != this.clientId) {
				return false
			}
			this.searchLoading = true
			this.apiCall({ destination: 'get_list_links_to', data: { link_type: 'MANUFACTURER', search_term: this.marquee.list_search_text }} ).then(obj => {
				this.searchLoading = false
				if (obj.status == 'error') {
					this.message = obj.message
				} else {
					// set the linksToList
					this.linksToList = obj.links_to
					// set the link_value to the first item in the linksToList
					if (this.linksToList.length > 0) {
						this.marquee.link_type_value = this.linksToList[0]['link_type_value']
					}
				}
			}).catch(err => {
				console.log(err)
			})
		},
		searchProducts() {
			if (this.clientId != 1 && this.marquee.client_id != this.clientId) {
				return false
			}
			this.searchLoading = true
			this.apiCall({ destination: 'get_list_links_to', data: { link_type: 'PRODUCT', search_term: this.marquee.list_search_text }} ).then(obj => {
				this.searchLoading = false
				if (obj.status == 'error') {
					this.message = obj.message
				} else {
					// set the linksToList
					this.linksToList = obj.links_to
					// set the link_value to the first item in the linksToList
					if (this.linksToList.length > 0) {
						this.marquee.link_type_value = this.linksToList[0]['product_id']
					}
				}
			}).catch(err => {
				console.log(err)
			})
		}
	}
}
</script>

<style lang="scss" scoped>
	.swap-marquee-image {
		margin: 0 0 1em 0;
		.button { width: 20em; }
	}
	.dropzone { width: 100%; height: calc(((100vw - 5em) / 3) - 0.9em); margin: 0 0 0.5em 0; }
	.drag { float: right; }
</style>
